/**
 * Parser for the value-expression mini-language used in sync field mappings.
 *
 * The full grammar is documented in the syntax help drawer (Step 3) and enforced
 * server-side by JsonMapper. This module's job is narrower: given a raw input
 * string and a cursor position, figure out what the user is currently typing
 * (context key / field / operator) so the autocomplete dropdown can offer
 * appropriate suggestions.
 *
 * We do NOT validate or fully tokenize — just scan enough to know what comes
 * next at the cursor.
 */

const SCALAR_TYPES = new Set([
  'String',
  'Date',
  'UUID',
  'Boolean',
  'Integer',
  'Long',
  'Double',
  'Float',
  'Short',
  'Byte',
  'BigDecimal',
  'BigInteger',
  'Number',
  'Object',
  'Any',
]);

function isIdentStart(ch) {
  return /[a-zA-Z_]/.test(ch);
}

function isIdentCont(ch) {
  return /[a-zA-Z0-9_]/.test(ch);
}

// Operators whose unquoted value can be either a path or a literal, distinguished by the
// first character of the value (BE heuristic: letter / `_` → path; digit / `-` / `+` →
// numeric or date literal; `'` / `"` → quoted string literal). When the cursor sits
// inside one of these AND the value looks like a path (or is empty, i.e. cursor right
// after `=`), we recursively tokenize so the user gets path autocomplete.
const PATH_VALUE_OPERATORS = new Set(['fallback', 'nullIf', 'nullIfAfter', 'nullIfBefore']);

// Inside an `?op=VALUE` cursor position: if the operator is path-accepting AND the value
// either is empty or starts with a path-start character, recursively tokenize the value
// text so the user gets path autocomplete. Otherwise returns `mode: 'none'`.
function tokenizeOperatorValueIfPath(body, valueStart, opName, segments) {
  if (!PATH_VALUE_OPERATORS.has(opName) || valueStart < 0) return makeNone(segments);
  const valueText = body.substring(valueStart);
  // Non-empty value with a non-path first char (digit, `-`, `+`, `'`, `"`, etc.) → the BE
  // will treat this as a literal; suppress path autocomplete to match.
  if (valueText.length > 0 && !isIdentStart(valueText[0])) return makeNone(segments);
  const sub = tokenizePlaceholder(valueText);
  return { ...sub, prefixStart: valueStart + sub.prefixStart };
}

/**
 * Strip a "List<X>" or "Collection<X>" wrapper, returning the element type name.
 * Plain (non-collection) names pass through unchanged.
 */
function unwrapCollection(typeName) {
  if (!typeName) return typeName;
  const m = /^(?:List|Set|Collection|Iterable)<(.+)>$/.exec(typeName);
  return m ? m[1] : typeName;
}

/**
 * Is the given type name "navigable" — does it have an entry in schema.types we
 * can descend into? Scalars and unknown types are leaves.
 */
export function isNavigable(typeName, schema) {
  if (!typeName) return false;
  const bare = unwrapCollection(typeName);
  if (SCALAR_TYPES.has(bare)) return false;
  // Synthetic Map<...> type used for existing.<column> — opaque.
  if (/^Map<.+>$/.test(typeName)) return false;
  return !!(schema && schema.types && schema.types[bare]);
}

/**
 * Walk a sequence of "consumed" path segments and return the resolved type at
 * the end. Used to figure out what type is in scope when the user types `.` or `?`.
 *
 * `segments` is a list of operations applied in order, each one of:
 *   { kind: 'context-key', name }    // first segment
 *   { kind: 'field', name }          // .fieldName
 *   { kind: 'subscript' }            // [...] — projects/indexes/filters a collection
 *
 * Returns { type: string|null, isCollection: boolean }.
 *  - `type` is the bare type name at the end (e.g. "String", "Bid", "Opportunity").
 *    null means the walk fell off (unknown context key or unknown field).
 *  - `isCollection` is true when we landed on a List/Set field that has NOT yet been
 *    consumed by a `[...]` subscript — informs whether the user can next write `[]`,
 *    and whether ?join is applicable.
 */
export function walkSegments(segments, schema) {
  let type = null;
  let isCollection = false;
  // `hasPendingProjection` stays true after a `[]` subscript is consumed: the engine is
  // now producing a list-of-leaves rather than a single scalar, so `?join` (and similar
  // list-applicable operators) remain relevant through subsequent field accesses, e.g.
  // `tender.bids[].tenderer.name` — the leaf type is String, but the engine output is a
  // list of strings that `?join` can collapse.
  let hasPendingProjection = false;
  for (const seg of segments) {
    if (seg.kind === 'context-key') {
      const ck = (schema.contextKeys || []).find((k) => k.name === seg.name);
      if (!ck) return { type: null, isCollection: false, hasPendingProjection: false };
      // Context-key types may already be wrapped (e.g. "List<DocumentFieldChange>").
      const m = /^(?:List|Set|Collection|Iterable)<(.+)>$/.exec(ck.type);
      if (m) {
        type = m[1];
        isCollection = true;
      } else {
        type = ck.type;
        isCollection = false;
      }
    } else if (seg.kind === 'field') {
      if (!type) return { type: null, isCollection: false, hasPendingProjection };
      const desc = schema.types && schema.types[type];
      if (!desc) return { type: null, isCollection: false, hasPendingProjection };
      const f = (desc.fields || []).find((field) => field.name === seg.name);
      if (!f) return { type: null, isCollection: false, hasPendingProjection };
      if (f.isCollection) {
        type = f.elementType || f.type;
        isCollection = true;
      } else {
        type = f.type;
        isCollection = false;
      }
    } else if (seg.kind === 'subscript') {
      // [] / [N] / [k=v]: consume the collection marker on the current segment, but
      // remember that we've entered projection mode — subsequent field accesses are
      // still per-element, so the overall output remains list-shaped.
      isCollection = false;
      hasPendingProjection = true;
    } else if (seg.kind === 'list-literal') {
      // `[a, b, c]` — inline list literal. Element types are heterogeneous and not
      // known here without recursing into each sub-expression; report a generic
      // collection so list-applicable operators (`?join`) and any-type operators
      // (`?fallback`, `?nullIf`) still surface in the operator autocomplete.
      type = null;
      isCollection = true;
    }
  }
  return { type, isCollection, hasPendingProjection };
}

/**
 * Given the raw text inside a {{...}} placeholder (from `{{` up to the cursor),
 * tokenize it well enough to determine what completion mode applies AT THE CURSOR.
 *
 * Returns:
 *   {
 *     mode: 'context-key' | 'field' | 'filter-key' | 'operator' | 'none',
 *     prefix: string,             // partial token under the cursor (for filtering suggestions)
 *     prefixStart: number,        // offset within the placeholder body where the prefix begins
 *                                 // (i.e. where the replacement should start)
 *     consumedSegments: Array,    // segments BEFORE the prefix — drives type resolution
 *     unconsumedCollection: bool, // true if the last consumed segment is a collection that
 *                                 // hasn't been subscripted yet (informs ?join applicability)
 *     bracketKeySegments?: Array  // (filter-key mode only) dotted-path field segments inside
 *                                 // the bracket BEFORE the prefix; drills into nested object
 *                                 // types per the back-end's filter-key resolution rules.
 *   }
 *
 * "consumedSegments" excludes the partial token at the cursor — that one is the prefix
 * being typed, not yet committed. `mode: 'none'` is returned for cursor positions where
 * autocomplete is suppressed but the input is still legal (e.g. inside `?op=VALUE` value
 * territory, after a bare numeric subscript like `[0]`, or right after `]`).
 */
// Handle the inline list-literal placeholder form: `[expr, expr, ...]?op?op...`. Two cases:
//   - Cursor still inside the `[...]`: find the current element (between the last top-level
//     `,` and the cursor) and recursively tokenize it as a normal placeholder body, so the
//     existing field/context-key/operator logic kicks in for whichever element is being typed.
//   - Cursor past the closing `]`: the list itself is the consumed segment, and we walk the
//     remaining `?op[=val]` text to figure out operator-mode state.
// Top-level scanning tracks bracket depth and quote state so nested subscripts (`bids[0]`)
// and quoted commas/brackets (`'a,b'`, `'foo]bar'`) inside elements don't confuse boundaries.
function tokenizeListLiteral(body) {
  let i = 1; // skip leading `[`
  let depth = 0;
  let quoteChar = null;
  let elementStart = 1;
  let closedAt = -1; // index right after the matching top-level `]`, or -1 if still open

  while (i < body.length) {
    const c = body[i];
    if (quoteChar) {
      if (c === quoteChar) quoteChar = null;
    } else if (c === "'" || c === '"') {
      quoteChar = c;
    } else if (c === '[') {
      depth++;
    } else if (c === ']') {
      if (depth === 0) {
        closedAt = i + 1;
        break;
      }
      depth--;
    } else if (c === ',' && depth === 0) {
      elementStart = i + 1;
    }
    i++;
  }

  if (closedAt === -1) {
    // Cursor still inside the list literal; recursively tokenize the current element.
    // Skip leading whitespace after a `,` so the prefix doesn't start with a space.
    let trimmedStart = elementStart;
    while (trimmedStart < body.length && body[trimmedStart] === ' ') trimmedStart++;
    const sub = tokenizePlaceholder(body.substring(trimmedStart));
    return {
      ...sub,
      prefixStart: trimmedStart + sub.prefixStart,
    };
  }

  // Cursor is past the closing `]` — operator territory. Walk the post-`]` text.
  return tokenizeAfterListLiteral(body, closedAt);
}

// After the closing `]` of a list literal, the syntax mirrors the post-subscript state in
// the main state machine: `?op`, `?op=val`, chained `?op=val?op`. We reuse those state
// transitions inline so the list literal can carry an `unconsumedCollection: true` flag
// (it IS a list) without having to fake a context-key segment.
function tokenizeAfterListLiteral(body, closedAt) {
  const segments = [{ kind: 'list-literal' }];
  const noneState = {
    mode: 'none',
    prefix: '',
    prefixStart: 0,
    consumedSegments: segments,
    unconsumedCollection: true,
  };
  let i = closedAt;
  let state = 'AFTER_BRACKET';
  let opNameStart = -1;
  let currentOpName = '';
  let opValueStart = -1;

  while (i < body.length) {
    const c = body[i];
    switch (state) {
      case 'AFTER_BRACKET':
        if (c === '?') state = 'AFTER_QUESTION';
        else return noneState;
        break;
      case 'AFTER_QUESTION':
        if (isIdentStart(c)) {
          opNameStart = i;
          state = 'AFTER_OP_NAME';
        } else {
          return noneState;
        }
        break;
      case 'AFTER_OP_NAME':
        if (isIdentCont(c)) {
          // keep reading
        } else if (c === '=') {
          currentOpName = body.substring(opNameStart, i);
          opValueStart = i + 1;
          state = 'AFTER_EQUALS';
        } else if (c === '?') {
          currentOpName = '';
          opValueStart = -1;
          state = 'AFTER_QUESTION';
        } else {
          return noneState;
        }
        break;
      case 'AFTER_EQUALS':
      case 'INSIDE_OP_VALUE':
        if (c === '?') {
          currentOpName = '';
          opValueStart = -1;
          state = 'AFTER_QUESTION';
        } else {
          state = 'INSIDE_OP_VALUE';
        }
        break;
    }
    i++;
  }

  switch (state) {
    case 'AFTER_BRACKET':
      return noneState;
    case 'AFTER_QUESTION':
      return {
        mode: 'operator',
        prefix: '',
        prefixStart: body.length,
        consumedSegments: segments,
        unconsumedCollection: true,
      };
    case 'AFTER_OP_NAME':
      return {
        mode: 'operator',
        prefix: body.substring(opNameStart),
        prefixStart: opNameStart,
        consumedSegments: segments,
        unconsumedCollection: true,
      };
    case 'AFTER_EQUALS':
    case 'INSIDE_OP_VALUE':
      // `?fallback=path` past a list literal: same sub-expression autocomplete as the
      // main state machine does for path-accepting operator values.
      return tokenizeOperatorValueIfPath(body, opValueStart, currentOpName, segments);
  }
  return noneState;
}

export function tokenizePlaceholder(body) {
  // Inline list-literal form: `[expr, expr, ...]?op?op...` — each element is its own
  // sub-expression (path or quoted literal). Handled separately because the cursor's
  // completion mode depends on which element it's in (or whether it's past the `]`).
  if (body.charAt(0) === '[') {
    return tokenizeListLiteral(body);
  }

  // Linear scan, state machine.
  // States: START | AFTER_IDENT | AFTER_DOT | INSIDE_BRACKET | AFTER_BRACKET | AFTER_QUESTION | AFTER_OP_NAME | AFTER_EQUALS | INSIDE_OP_VALUE
  let i = 0;
  const segments = [];
  let identStart = -1; // when reading an identifier, where it began
  let isFirstIdent = true; // first ident = context key; subsequent = field
  let state = 'START';
  let opNameStart = -1;
  let bracketStart = -1; // position right after the most recent `[` (used for filter-key autocomplete)
  let quoteChar = null; // inside a `[...]` quoted value: the active quote char ('|"), null otherwise
  let currentOpName = ''; // name of the operator whose `=VALUE` we're currently inside (path-value autocomplete)
  let opValueStart = -1; // index of the first char of the current operator value (right after `=`)

  // Commit a "consumed" identifier into segments (used when we definitively see a
  // delimiter after the ident, so it's no longer the partial-under-cursor).
  const commitIdent = (end) => {
    const name = body.substring(identStart, end);
    if (!name) return;
    segments.push({ kind: isFirstIdent ? 'context-key' : 'field', name });
    isFirstIdent = false;
    identStart = -1;
  };

  while (i < body.length) {
    const c = body[i];
    switch (state) {
      case 'START':
        if (isIdentStart(c)) {
          identStart = i;
          state = 'AFTER_IDENT';
        } else {
          // Garbage at start — bail; suggest context keys with empty prefix.
          return {
            mode: 'context-key',
            prefix: '',
            prefixStart: 0,
            consumedSegments: [],
            unconsumedCollection: false,
          };
        }
        break;

      case 'AFTER_IDENT':
        if (isIdentCont(c)) {
          // keep reading
        } else if (c === '.') {
          commitIdent(i);
          state = 'AFTER_DOT';
        } else if (c === '[') {
          commitIdent(i);
          bracketStart = i + 1;
          state = 'INSIDE_BRACKET';
        } else if (c === '?') {
          commitIdent(i);
          state = 'AFTER_QUESTION';
        } else {
          // Unknown char — bail.
          return makeNone(segments);
        }
        break;

      case 'AFTER_DOT':
        if (isIdentStart(c)) {
          identStart = i;
          state = 'AFTER_IDENT';
        } else {
          return makeNone(segments);
        }
        break;

      case 'INSIDE_BRACKET':
        // Quoted-value-aware: `]` only closes the subscript when we're NOT inside a quoted
        // filter value, since the syntax allows e.g. `[name='foo]bar']` (quotes are how
        // the user includes a literal `]` in the value).
        if (quoteChar) {
          if (c === quoteChar) quoteChar = null;
          // else: still inside the quoted value
        } else if (c === "'" || c === '"') {
          quoteChar = c;
        } else if (c === ']') {
          segments.push({ kind: 'subscript' });
          state = 'AFTER_BRACKET';
        }
        // else: stay inside (any char allowed: digits, identifiers, =, !=, etc.)
        break;

      case 'AFTER_BRACKET':
        if (c === '.') {
          state = 'AFTER_DOT';
        } else if (c === '[') {
          bracketStart = i + 1;
          state = 'INSIDE_BRACKET';
        } else if (c === '?') {
          state = 'AFTER_QUESTION';
        } else {
          return makeNone(segments);
        }
        break;

      case 'AFTER_QUESTION':
        if (isIdentStart(c)) {
          opNameStart = i;
          state = 'AFTER_OP_NAME';
        } else {
          return makeNone(segments);
        }
        break;

      case 'AFTER_OP_NAME':
        if (isIdentCont(c)) {
          // keep reading op name
        } else if (c === '=') {
          currentOpName = body.substring(opNameStart, i);
          opValueStart = i + 1;
          state = 'AFTER_EQUALS';
        } else if (c === '?') {
          // chained operator (no value); start a new one
          currentOpName = '';
          opValueStart = -1;
          state = 'AFTER_QUESTION';
        } else {
          return makeNone(segments);
        }
        break;

      case 'AFTER_EQUALS':
      case 'INSIDE_OP_VALUE':
        if (c === '?') {
          currentOpName = '';
          opValueStart = -1;
          state = 'AFTER_QUESTION';
        } else {
          state = 'INSIDE_OP_VALUE';
        }
        break;
    }
    i++;
  }

  // End of input — figure out the mode based on where the scan landed.
  switch (state) {
    case 'START':
      return { mode: 'context-key', prefix: '', prefixStart: 0, consumedSegments: [], unconsumedCollection: false };
    case 'AFTER_IDENT': {
      // Partial identifier at the cursor.
      const prefix = body.substring(identStart);
      return {
        mode: isFirstIdent ? 'context-key' : 'field',
        prefix,
        prefixStart: identStart,
        consumedSegments: segments,
        unconsumedCollection: false,
      };
    }
    case 'AFTER_DOT':
      return {
        mode: 'field',
        prefix: '',
        prefixStart: body.length,
        consumedSegments: segments,
        unconsumedCollection: false,
      };
    case 'INSIDE_BRACKET': {
      // Inside `[...]` the user can write:
      //   - a numeric index (`[0]`)         → no useful autocomplete
      //   - a key=value filter (`[k=v]`)    → key side is a path into the element type
      //   - a key!=value exclude (`[k!=v]`)  → same
      //   - a quoted-value filter (`[k='…']`) → same
      // Filter keys may be dotted paths into nested objects (e.g. `tenderer.address.region`),
      // following the back-end's path-resolution rules. They can't traverse list-typed fields.
      const inside = bracketStart >= 0 ? body.substring(bracketStart) : '';
      // Bare numeric index — skip autocomplete.
      if (/^\s*\d/.test(inside)) return makeNone(segments);
      // Past an =/!= — value territory.
      if (/[!=]=?/.test(inside)) return makeNone(segments);
      // Dotted-key path: split on `.`; everything before the last segment is consumed
      // navigation (drill into nested objects), the last segment is the prefix being typed.
      const parts = inside.split('.');
      const partialPrefix = parts[parts.length - 1];
      const consumedKeyParts = parts.slice(0, -1);
      const bracketKeySegments = consumedKeyParts.map((name) => ({ kind: 'field', name }));
      // Position where the partial prefix begins, relative to the placeholder body.
      const consumedLen = consumedKeyParts.reduce((acc, p) => acc + p.length + 1, 0);
      const prefixStart = (bracketStart >= 0 ? bracketStart : body.length) + consumedLen;
      return {
        mode: 'filter-key',
        prefix: partialPrefix,
        prefixStart,
        consumedSegments: segments,
        bracketKeySegments,
        unconsumedCollection: false,
      };
    }
    case 'AFTER_BRACKET':
      // Just closed a subscript — could go to . or ? next; nothing meaningful to suggest yet.
      return makeNone(segments);
    case 'AFTER_QUESTION':
      return {
        mode: 'operator',
        prefix: '',
        prefixStart: body.length,
        consumedSegments: segments,
        unconsumedCollection: false,
      };
    case 'AFTER_OP_NAME': {
      const prefix = body.substring(opNameStart);
      return {
        mode: 'operator',
        prefix,
        prefixStart: opNameStart,
        consumedSegments: segments,
        unconsumedCollection: false,
      };
    }
    case 'AFTER_EQUALS':
    case 'INSIDE_OP_VALUE':
      // `?fallback=path` accepts a sub-expression; recurse so the user gets path autocomplete.
      // Other operators (`?format=`, `?join=`, etc.) take literal values — fall back to `none`.
      return tokenizeOperatorValueIfPath(body, opValueStart, currentOpName, segments);
  }
  return makeNone(segments);
}

function makeNone(segments) {
  return { mode: 'none', prefix: '', prefixStart: 0, consumedSegments: segments, unconsumedCollection: false };
}

/**
 * Given the full input value and a cursor offset, determine whether the cursor sits
 * inside a {{...}} placeholder and, if so, return the placeholder-body span and the
 * substring from `{{` to the cursor.
 *
 * Returns null when the cursor is in literal-text territory (no `{{` before, OR a
 * `}}` already closed the most recent one).
 */
export function findPlaceholderAtCursor(input, cursor) {
  let openIdx = -1;
  for (let i = cursor - 2; i >= 0; i--) {
    if (input[i] === '}' && input[i + 1] === '}') {
      return null; // a closing brace already happened between us and any open
    }
    if (input[i] === '{' && input[i + 1] === '{') {
      openIdx = i;
      break;
    }
  }
  if (openIdx === -1) return null;
  return {
    openIdx, // index of the first '{' of '{{'
    bodyStart: openIdx + 2, // index of the first char of the body
    body: input.substring(openIdx + 2, cursor),
  };
}

/**
 * Top-level: given the raw input, the cursor, and the cached schema, return a
 * suggestion descriptor for the UI. When `null`, the autocomplete dropdown should
 * stay closed.
 *
 * The result includes everything needed to render and insert:
 *   - mode: which kind of suggestions ('context-key' | 'field' | 'filter-key' | 'operator' | 'none')
 *   - prefix: the partial token to match against
 *   - replaceStart / replaceEnd: absolute offsets in `input` where a chosen
 *     suggestion's text should be spliced in
 *   - resolvedType: the bare type name at the cursor (drives operator filtering;
 *     in filter-key mode, this is the type reached after walking bracketKeySegments)
 *   - unconsumedCollection: true when the cursor sits on a collection that hasn't
 *     been subscripted yet (list operators like ?join apply)
 *   - hasPendingProjection: true when a `[]` projection has been consumed but the
 *     engine output is still list-shaped — list-applicable operators still apply
 *     (e.g. `tender.bids[].tenderer.name?join=`)
 *   - inExistingMap: true when the user is dotting off `existing` (no field
 *     suggestions; BC column names are dynamic per profile)
 */
export function getAutocompleteState(input, cursor, schema) {
  if (!schema || !schema.contextKeys) return null;
  const placeholder = findPlaceholderAtCursor(input, cursor);
  if (!placeholder) return null;

  const tok = tokenizePlaceholder(placeholder.body);

  // Resolve the type at the cursor's "consumed" prefix.
  const walked = walkSegments(tok.consumedSegments, schema);

  // For filter-key mode, the dotted-path segments inside the bracket drill into the
  // collection's element type. Walk them to find the type at the cursor.
  let resolvedType = walked.type;
  if (tok.mode === 'filter-key' && Array.isArray(tok.bracketKeySegments)) {
    for (const seg of tok.bracketKeySegments) {
      if (!resolvedType) break;
      const desc = schema.types && schema.types[resolvedType];
      if (!desc) {
        resolvedType = null;
        break;
      }
      const f = (desc.fields || []).find((field) => field.name === seg.name);
      if (!f || f.isCollection) {
        // Field not found, or list-typed (can't traverse lists in filter keys).
        resolvedType = null;
        break;
      }
      resolvedType = f.type;
    }
  }

  // Special case: dotting off `existing` — the columns are dynamic per-profile,
  // so we suppress field suggestions and let the user type freely.
  const lastCtx = tok.consumedSegments[0];
  const isExisting = lastCtx && lastCtx.kind === 'context-key' && lastCtx.name === 'existing';
  const inExistingMap = isExisting && tok.mode === 'field';

  return {
    mode: tok.mode,
    prefix: tok.prefix,
    replaceStart: placeholder.bodyStart + tok.prefixStart,
    replaceEnd: cursor,
    resolvedType,
    unconsumedCollection: walked.isCollection,
    hasPendingProjection: walked.hasPendingProjection,
    inExistingMap,
  };
}

/**
 * Produce the list of suggestion items for the dropdown based on parser output.
 * Each item has:
 *   - label:        what the user reads in the row
 *   - typeLabel:    small chip showing the type (or operator example)
 *   - insertText:   what gets inserted into the input (without the trigger char)
 *   - cursorOffset: where the cursor lands after insert, relative to the END of insertText
 *                   (negative = move left; useful for operator templates ending in `=`)
 *   - description:  hovercard / sidebar copy
 *   - availability: optional badge key (TENDER_UPDATE_ONLY, BC_PATCH_ONLY, etc.)
 *   - id:           stable key for v-for
 */
export function buildSuggestions(state, schema, { availabilityLabels } = {}) {
  if (!state) return [];
  if (state.mode === 'context-key') {
    return (schema.contextKeys || [])
      .filter((k) => k.name.toLowerCase().startsWith(state.prefix.toLowerCase()))
      .map((k) => {
        // Context keys whose type is wrapped in List<...> are collections; insert `[]`
        // and land the cursor between the brackets so the user is positioned for an
        // index, filter, or empty-projection (a single `→` press) without backspacing.
        const isList = /^(?:List|Set|Collection|Iterable)<.+>$/.test(k.type);
        return {
          id: `ctx:${k.name}`,
          label: k.name,
          typeLabel: k.type,
          insertText: isList ? `${k.name}[]` : k.name,
          cursorOffset: isList ? -1 : 0,
          description: k.description || '',
          availability: k.availability !== 'ALWAYS' ? availabilityLabels?.[k.availability] || k.availability : null,
        };
      });
  }

  if (state.mode === 'field') {
    if (state.inExistingMap) return []; // BC columns are dynamic — no suggestions
    const typeDesc = state.resolvedType && schema.types && schema.types[state.resolvedType];
    if (!typeDesc) return [];
    return (typeDesc.fields || [])
      .filter((f) => f.name.toLowerCase().startsWith(state.prefix.toLowerCase()))
      .map((f) => ({
        id: `field:${state.resolvedType}.${f.name}`,
        label: f.name,
        typeLabel: f.isCollection ? `List<${f.elementType}>` : f.type,
        // Collection fields → insert `name[]` with cursor inside the brackets.
        // The user is positioned for the most common next steps (projection, index, filter).
        insertText: f.isCollection ? `${f.name}[]` : f.name,
        cursorOffset: f.isCollection ? -1 : 0,
        description: '',
        availability: null,
      }));
  }

  if (state.mode === 'filter-key') {
    // Fields of the type at the cursor (which is the collection's element type for the
    // first segment, or a nested object for deeper dotted-key segments).
    // Engine accepts dotted paths in filter keys but can't traverse list-typed fields,
    // so we drop collections from the suggestions. Object-typed fields are kept — the
    // user can drill into them; we insert the trailing `.` so the next dropdown opens
    // naturally on the next nested type. Scalar fields get the trailing `=` so the
    // cursor lands ready for the value.
    const typeDesc = state.resolvedType && schema.types && schema.types[state.resolvedType];
    if (!typeDesc) return [];
    return (typeDesc.fields || [])
      .filter((f) => !f.isCollection)
      .filter((f) => f.name.toLowerCase().startsWith(state.prefix.toLowerCase()))
      .map((f) => {
        const isObject = isNavigable(f.type, schema);
        return {
          id: `filter:${state.resolvedType}.${f.name}`,
          label: f.name,
          typeLabel: f.type,
          insertText: isObject ? `${f.name}.` : `${f.name}=`,
          cursorOffset: 0,
          description: '',
          availability: null,
        };
      });
  }

  if (state.mode === 'operator') {
    const operators = schema.operators || [];
    const filterPrefix = state.prefix.toLowerCase();
    return operators
      .filter((op) => op.name.toLowerCase().startsWith(filterPrefix))
      .filter((op) => operatorApplies(op, state))
      .map((op) => {
        const insertText = op.takesValue ? `${op.name}=` : op.name;
        return {
          id: `op:${op.name}`,
          label: `?${op.name}`,
          typeLabel: op.syntax || (op.takesValue ? '?op=VALUE' : '?op'),
          insertText,
          cursorOffset: 0, // cursor lands after `=`, ready for the user to type the value
          description: op.description || '',
          availability: null,
        };
      });
  }

  return [];
}

function operatorApplies(op, state) {
  // BE schema endpoint has used both names over time; accept either so a renamed contract
  // doesn't silently disable the type-match filter.
  const appliesTo = op.appliesTo || op.applicableTypes || [];
  if (appliesTo.includes('any')) return true;
  // List-applicable operators (?join, etc.) are relevant when the cursor's path either
  // points at an unsubscripted collection (e.g. `tender.bids?`) OR has gone through a
  // projection that's still pending collapse (e.g. `tender.bids[].tenderer.name?` —
  // leaf type is String, but the engine produces a list of strings).
  if (appliesTo.includes('List') && (state.unconsumedCollection || state.hasPendingProjection)) return true;
  // Specific scalar types.
  if (state.resolvedType && appliesTo.includes(state.resolvedType)) return true;
  return false;
}
